import { z } from 'zod'
import { AccountExportResponse } from './api.js'

/**
 * The account-action family — SM5 (B7.5, B8, §13.5; MCP-AC-074, D14).
 *
 * One prepare, four actions, and every one of them ends where the *person* is:
 *
 * 1. **The prepare performs nothing.** `performed` is the literal `false` on every result, and there
 *    is no commit tool for this family. What the prepare mints is a **secure action-specific
 *    continuation** — a frozen action row plus the browser surface that completes it — so a model
 *    can start a disconnect, a grant change, an export or a deletion and can never finish one. That
 *    is what MCP-AC-074 asks for by name ("prepare-only tool path").
 * 2. **The continuation carries no authority.** The URL is the person's own surface, the token is
 *    the person's own session, and the three `carries_*` literals say so in the payload: a host
 *    cannot read send, sign or delete authority into the handle it was handed.
 * 3. **Deletion always reauthenticates, and this surface never deletes.** `DELETE_ACCOUNT`'s
 *    continuation is the store-required external page (`/account/deletion`) and its completion
 *    reauthenticates first — and even then it answers `completed: false` with the external step,
 *    because the deletion disposition belongs to the surface that reauthenticated the person, not
 *    to a model-prepared handle. Nothing here can fabricate a completed deletion.
 * 4. **Nothing here can fabricate a revoked grant either.** The two host actions are performed by
 *    the person's own reauthenticated call, and what that call answers with is the grant row
 *    **read back** after the change — never the value the caller asked for.
 */

const Id = z.string().min(1).max(128)
const IsoDateTime = z.string().datetime()
const Route = z.string().min(1).max(200)

/** The four actions the family declares. `account.manage` is the scope for all of them. */
export const MCP_ACCOUNT_ACTION_KINDS = [
  'DISCONNECT_HOST',
  'MANAGE_HOST_GRANT',
  'EXPORT_ACCOUNT',
  'DELETE_ACCOUNT',
] as const
export const McpAccountActionKind = z.enum(MCP_ACCOUNT_ACTION_KINDS)
export type McpAccountActionKind = z.infer<typeof McpAccountActionKind>

/**
 * The frozen action's lifecycle. `PERFORMED` is reachable only for the two host actions and the
 * export; the deletion's completion records `HANDED_OFF` instead — the person was handed to the
 * external page that performs that disposition, and this service performed nothing. No row ever
 * reads `PERFORMED` for a deletion.
 */
export const MCP_ACCOUNT_ACTION_STATES = [
  'AWAITING_PERSON',
  'HANDED_OFF',
  'PERFORMED',
  'EXPIRED',
  'CANCELLED',
] as const
export const McpAccountActionState = z.enum(MCP_ACCOUNT_ACTION_STATES)
export type McpAccountActionState = z.infer<typeof McpAccountActionState>

/**
 * The bound on one host's selected-item grant. It is the grant surface's own bound, named here so
 * the account-action schema and the connection schema cannot drift apart.
 */
export const MCP_ACCOUNT_ACTION_MAX_ITEM_IDS = 100

/**
 * The continuation's window. Long enough for a person to read an emailed code and complete a
 * deletion in the same browser session, short enough that a handle left in a chat log is dead
 * before it is useful to anyone else.
 */
export const MCP_ACCOUNT_ACTION_TTL_SECONDS = 30 * 60

/** The browser surfaces the four actions complete on. Named, so the payload never carries a literal. */
export const MCP_ACCOUNT_ACTION_SURFACES = ['TRUSTED_SURFACE', 'EXTERNAL_WEB_PAGE'] as const
export const McpAccountActionSurface = z.enum(MCP_ACCOUNT_ACTION_SURFACES)
export type McpAccountActionSurface = z.infer<typeof McpAccountActionSurface>

/* --------------------------------- input ---------------------------------- */

/**
 * The prepare request. `grant_id` is required by the two host actions and refused by the other two,
 * and `item_ids` is required by `MANAGE_HOST_GRANT` alone — so an action cannot be prepared with a
 * target it does not act on, and a target cannot be smuggled into an action that ignores it.
 */
export const accountActionPrepareInput = z
  .object({
    action: McpAccountActionKind,
    /** The host grant this action is about: the whole connection, or the item grant it changes. */
    grant_id: Id.optional(),
    /** The exact selected-item set the host's grant becomes. `MANAGE_HOST_GRANT` only. */
    item_ids: z.array(Id).max(MCP_ACCOUNT_ACTION_MAX_ITEM_IDS).optional(),
    client_request_id: z.string().min(1).max(128),
  })
  .strict()
  .superRefine((input, ctx) => {
    const targeted = input.action === 'DISCONNECT_HOST' || input.action === 'MANAGE_HOST_GRANT'
    if (targeted && input.grant_id === undefined) {
      ctx.addIssue({
        code: 'custom',
        message: 'this action names the host grant it acts on',
        path: ['grant_id'],
      })
    }
    if (!targeted && input.grant_id !== undefined) {
      ctx.addIssue({
        code: 'custom',
        message: 'this action takes no host grant',
        path: ['grant_id'],
      })
    }
    if (input.action === 'MANAGE_HOST_GRANT' && input.item_ids === undefined) {
      ctx.addIssue({
        code: 'custom',
        message: 'a grant change names the item set',
        path: ['item_ids'],
      })
    }
    if (input.action !== 'MANAGE_HOST_GRANT' && input.item_ids !== undefined) {
      ctx.addIssue({
        code: 'custom',
        message: 'only a grant change names an item set',
        path: ['item_ids'],
      })
    }
  })
export type AccountActionPrepareInput = z.infer<typeof accountActionPrepareInput>

/* --------------------------------- result --------------------------------- */

/** The host a grant belongs to, in the vocabulary `signbento_get_connection_activity` already uses. */
export const McpAccountActionHost = z
  .object({
    client_id: z.string().min(1).max(512),
    client_name: z.string().min(1).max(200),
    distribution: z.enum(['openai', 'claude', 'other']),
  })
  .strict()
export type McpAccountActionHost = z.infer<typeof McpAccountActionHost>

/**
 * What the frozen action is about, as it was read at prepare: the grant, the host identity the
 * server resolved for it, and — for a grant change — the item set the person is being asked to
 * confirm. Never an id the caller supplied without the server having resolved it.
 */
export const McpAccountActionTarget = z
  .object({
    grant_id: Id.nullable(),
    host: McpAccountActionHost.nullable(),
    item_ids: z.array(Id).nullable(),
  })
  .strict()
export type McpAccountActionTarget = z.infer<typeof McpAccountActionTarget>

/**
 * The secure continuation. `single_use` is a literal because the action row is what enforces it: a
 * second completion of the same action answers `TERMINAL_STATE` rather than performing it twice.
 */
export const McpAccountActionContinuation = z
  .object({
    /** The person's own browser surface — never an API route a model can call. */
    url: z.string().min(1).max(1024),
    expires_at: IsoDateTime,
    single_use: z.literal(true),
    surface: McpAccountActionSurface,
    /** Stated because it is the authority: the continuation is the session, not a token in a URL. */
    needs_session: z.literal(true),
    needs_reauthentication: z.boolean(),
    /** Literals, so a host cannot read authority into the handle it was handed. */
    carries_send_authority: z.literal(false),
    carries_sign_authority: z.literal(false),
    carries_delete_authority: z.literal(false),
  })
  .strict()
export type McpAccountActionContinuation = z.infer<typeof McpAccountActionContinuation>

/** What the person does next, as machine values rather than prose. */
export const McpAccountActionNextStep = z
  .object({
    kind: z.enum(['COMPLETE_IN_TRUSTED_SURFACE', 'COMPLETE_IN_EXTERNAL_PAGE']),
    route: Route,
    needs_session: z.literal(true),
    needs_reauthentication: z.boolean(),
  })
  .strict()
export type McpAccountActionNextStep = z.infer<typeof McpAccountActionNextStep>

/**
 * The prepare's answer. `performed` is the literal `false`: this call froze an action and handed the
 * person a continuation, and no reader can mistake the payload for a completed revocation, export or
 * deletion.
 */
export const McpAccountActionPrepareResult = z
  .object({
    action_id: Id,
    action: McpAccountActionKind,
    state: McpAccountActionState,
    requires_reauthentication: z.boolean(),
    target: McpAccountActionTarget.nullable(),
    continuation: McpAccountActionContinuation,
    next_step: McpAccountActionNextStep,
    performed: z.literal(false),
    idempotent_replay: z.boolean(),
  })
  .strict()
export type McpAccountActionPrepareResult = z.infer<typeof McpAccountActionPrepareResult>

/**
 * What the person's page reads before completing: the frozen action and the target the server
 * resolved, so the person checks the host and the item set before confirming.
 */
export const McpAccountActionRead = z
  .object({
    action_id: Id,
    action: McpAccountActionKind,
    state: McpAccountActionState,
    expires_at: IsoDateTime,
    requires_reauthentication: z.boolean(),
    target: McpAccountActionTarget.nullable(),
  })
  .strict()
export type McpAccountActionRead = z.infer<typeof McpAccountActionRead>

/* ------------------------------- completion -------------------------------- */

/** A grant as the completion answers with it: the row read back after the change, never the ask. */
export const McpAccountActionGrantState = z
  .object({
    grant_id: Id,
    state: z.enum(['ACTIVE', 'PENDING_CONSENT', 'REVOKED', 'EXPIRED']),
    item_scope_mode: z.enum(['ALL_PUBLISHED', 'SELECTED_ITEMS']),
    item_ids: z.array(Id),
    authorization_revision: z.number().int().positive(),
  })
  .strict()
export type McpAccountActionGrantState = z.infer<typeof McpAccountActionGrantState>

/**
 * The person's own completion. It is **not** an MCP result: it is what the session-authenticated
 * route answers, so `reauthenticated_at` is a fact about the person's own proof rather than anything
 * a host asserted.
 *
 * `completed` is false for a deletion, always: the deletion disposition belongs to the external page
 * this route hands the person to, and this payload says so with the next step instead of claiming
 * an outcome.
 */
export const McpAccountActionCompletion = z
  .object({
    action_id: Id,
    action: McpAccountActionKind,
    state: McpAccountActionState,
    completed: z.boolean(),
    reauthenticated_at: IsoDateTime,
    /** The grant, read back after a host action. `null` for the two account-level actions. */
    grant: McpAccountActionGrantState.nullable(),
    /** The export body, when the action was the export. `null` otherwise. */
    export: AccountExportResponse.nullable(),
    /** Present when the person still has a step to take. `null` once there is nothing left. */
    next_step: McpAccountActionNextStep.nullable(),
  })
  .strict()
export type McpAccountActionCompletion = z.infer<typeof McpAccountActionCompletion>

/**
 * The two host actions that a person's own session can complete, and the two that always
 * reauthenticate. Named once here so the tool's `requires_reauthentication`, the continuation's
 * literal and the route's guard cannot disagree.
 */
export function accountActionRequiresReauthentication(action: McpAccountActionKind): boolean {
  return action === 'EXPORT_ACCOUNT' || action === 'DELETE_ACCOUNT'
}

/** The two actions that act on a host grant. */
export function accountActionTargetsHost(action: McpAccountActionKind): boolean {
  return action === 'DISCONNECT_HOST' || action === 'MANAGE_HOST_GRANT'
}
